import * as React from 'react'
import type {GetApiBase, IApiPathPieces, TGetQueryParameters} from '~/api/client'
import APILoggedOutError from '~/api/client/APIError/APILoggedOutError'
import type APIError from '~/api/client/APIError/base'
import type {IJSONObject} from '~/api/client/types'
import PromiseManager from '~/components/PromiseManager'
import {navigateTo} from '~/routing/navigate'
import {staticURLs} from '~/routing/sitemap'

interface IProps<
  RequestParamsType extends TGetQueryParameters,
  ResponseType extends IJSONObject,
  PathPiecesType extends IApiPathPieces,
  ResultType = ResponseType,
> {
  params: RequestParamsType
  pathPieces: PathPiecesType
  /**
   * Sometimes, you may want to get a new promise result without changing the params/pathPieces.
   * By setting/changing this prop, you can force the manager to re-request the API.
   */
  refreshTrigger?: any

  /**
   * Provider function called with the result of the promise.  Return value will be rendered.
   * If you don't provide this function and/or `catch`, no loading UI will be rendered in the 'loading' state.
   */
  then?: (result: ResultType) => JSX.Element | JSX.Element[]

  /**
   * Provider function called with the result of a failed promise. Return value will be rendered.
   * If you don't provide this function and/or `then`, no loading UI will be rendered in the 'loading' state.
   */
  catch?: (error: any) => JSX.Element | JSX.Element[]

  /** Function called with the result of the promise as it changes, useful to set state outside of render() */
  onSuccess?: (result: ResultType) => void
  onLoading?: () => void
  onFailed?: (errors: APIError) => void
}

/**
 * This class builder shouldn't be used directly.
 * Instead you can use the version of this on any
 * GetApi by using the `PromiseManager` member variable
 * The generic types are just passed through to the GetApi
 */
export const buildGetApiPromiseManager = <
  RequestParamsType extends TGetQueryParameters,
  ResponseType extends IJSONObject,
  PathPiecesType extends IApiPathPieces,
  ResultType = ResponseType,
>(
  api: GetApiBase<RequestParamsType, ResponseType, PathPiecesType, ResultType>
) => {
  return class GetApiPromiseManager extends React.Component<
    IProps<RequestParamsType, ResponseType, PathPiecesType, ResultType>
  > {
    abortController!: AbortController

    componentDidMount() {
      this.abortController = new AbortController()
    }

    componentWillUnmount() {
      /* cancel this promise on unmount */
      this.abortController.abort()
    }

    render() {
      return (
        <PromiseManager<
          ResultType,
          {
            params: RequestParamsType
            pathPieces: PathPiecesType
            refreshTrigger?: any
          }
        >
          promiseProps={{
            params: this.props.params,
            pathPieces: this.props.pathPieces,
            refreshTrigger: this.props.refreshTrigger,
          }}
          generatePromise={promiseProps => {
            const apiCall = api.execute(
              promiseProps.params,
              promiseProps.pathPieces,
              this.abortController.signal
            )

            apiCall.catch((e: APIError) => {
              if (e instanceof APILoggedOutError) {
                // in the case of a logged-out error we should force logout the user
                // otherwise they are going to get weird errors
                console.warn('Session timed out. You must log in again to continue.')
                navigateTo(staticURLs.auth.signout)
              }
            })

            return apiCall
          }}
          then={this.props.then}
          catch={this.props.catch}
          onSuccess={this.props.onSuccess}
          onFailed={this.props.onFailed}
          onLoading={this.props.onLoading}
        />
      )
    }
  }
}
